// Settings.jsx - обновленный с кэшированием и событиями
import React, { useState, useEffect, useCallback } from 'react';
import './Settings.css';
import { useDataCache } from '../../context/DataCacheContext';
import { useCachedData } from '../../hooks/useDataCache';
import { useAuth, PERMISSIONS } from '../../context/AuthContext';
import { useDataEvents } from '../../hooks/useDataEvents';
import { EVENTS, dataEvents } from '../../hooks/useDataEvents';

import EmployeesTab from './tabs/EmployeesTab';
import PositionsTab from './tabs/PositionsTab';
import TrainingsTab from './tabs/TrainingsTab';
import MatrixTab from './tabs/MatrixTab';
// import ProcessTab from './tabs/ProcessTab'; // УДАЛЕНО
import UsersTab from './tabs/UsersTab';

const Settings = () => {
  const { hasPermission, userRole } = useAuth();
  const [activeTab, setActiveTab] = useState('employees');
  const [refreshing, setRefreshing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    positions: false,
    positionCategories: false,
    courses: false,
    categories: false,
    employees: false,
    matrix: false
  });
  
  const { 
    positions, 
    positionCategories,
    courses, 
    employees,
    categories,
    matrix,
    refreshAllData,
    fetchPositions,
    fetchPositionCategories,
    fetchCourses,
    fetchCategories,
    fetchEmployees,
    fetchMatrix,
    updateMatrixAssignment
  } = useDataCache();
  
  const { getDataWithStatus } = useCachedData();

  const positionsStatus = getDataWithStatus(positions);
  const positionCategoriesStatus = getDataWithStatus(positionCategories);
  const coursesStatus = getDataWithStatus(courses);
  const employeesStatus = getDataWithStatus(employees);
  const categoriesStatus = getDataWithStatus(categories);
  const matrixStatus = getDataWithStatus(matrix);

  // Подписка на события изменения данных
  useDataEvents({
    [EVENTS.EMPLOYEE_CREATED]: () => fetchEmployees({ force: true }),
    [EVENTS.EMPLOYEE_UPDATED]: () => fetchEmployees({ force: true }),
    [EVENTS.EMPLOYEE_DELETED]: () => fetchEmployees({ force: true }),
    [EVENTS.POSITION_CREATED]: () => {
      fetchPositions({ force: true });
      fetchPositionCategories({ force: true });
    },
    [EVENTS.POSITION_UPDATED]: () => {
      fetchPositions({ force: true });
      fetchPositionCategories({ force: true });
    },
    [EVENTS.COURSE_CREATED]: () => {
      fetchCourses({ force: true });
      fetchCategories({ force: true });
    },
    [EVENTS.COURSE_UPDATED]: () => {
      fetchCourses({ force: true });
      fetchCategories({ force: true });
    },
    [EVENTS.MATRIX_UPDATED]: () => fetchMatrix({ force: true }),
    [EVENTS.ALL_DATA_CHANGED]: () => {
      fetchEmployees({ force: true });
      fetchPositions({ force: true });
      fetchPositionCategories({ force: true });
      fetchCourses({ force: true });
      fetchCategories({ force: true });
      fetchMatrix({ force: true });
    }
  });

  // Данные из кеша для передачи в табы
  const positionsData = positionsStatus.data;
  const positionCategoriesData = positionCategoriesStatus.data;
  const coursesData = coursesStatus.data;
  const employeesData = employeesStatus.data;
  const categoriesData = categoriesStatus.data;
  const matrixData = matrixStatus.data;

  // Обновление состояний загрузки
  useEffect(() => {
    setLoadingStates({
      positions: positionsStatus.loading,
      positionCategories: positionCategoriesStatus.loading,
      courses: coursesStatus.loading,
      categories: categoriesStatus.loading,
      employees: employeesStatus.loading,
      matrix: matrixStatus.loading
    });
  }, [
    positionsStatus.loading,
    positionCategoriesStatus.loading,
    coursesStatus.loading,
    categoriesStatus.loading,
    employeesStatus.loading,
    matrixStatus.loading
  ]);

  // Подсчет загруженных данных
  const loadedCount = Object.values(loadingStates).filter(loading => !loading).length;
  const totalCount = Object.keys(loadingStates).length;
  const loadingPercentage = Math.round((loadedCount / totalCount) * 100);
  const allDataLoaded = loadedCount === totalCount;

  // Загрузка департаментов
  const departments = React.useMemo(() => {
    const deptMap = new Map();
    
    if (positionsData && positionsData.length > 0) {
      positionsData.forEach(pos => {
        if (pos.departments && Array.isArray(pos.departments)) {
          pos.departments.forEach(dept => {
            if (dept.id && dept.name && !deptMap.has(dept.id)) {
              deptMap.set(dept.id, {
                id: dept.id,
                name: dept.name
              });
            }
          });
        }
      });
    }
    
    if (employeesData && employeesData.length > 0) {
      employeesData.forEach(emp => {
        if (emp.department_id && emp.department && emp.department !== '—') {
          if (!deptMap.has(emp.department_id)) {
            deptMap.set(emp.department_id, {
              id: emp.department_id,
              name: emp.department
            });
          }
        }
      });
    }
    
    return Array.from(deptMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [positionsData, employeesData]);

  const showInitialLoading = !isInitialized && 
    (positionsStatus.loading || coursesStatus.loading || employeesStatus.loading);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshAllData();
    } finally {
      setRefreshing(false);
    }
  };

  const handleDataChange = useCallback(async (fetchFunction, eventType) => {
    if (fetchFunction) {
      await fetchFunction({ force: true });
    }
    if (eventType) {
      dataEvents.emit(eventType);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadAllData = async () => {
      await Promise.allSettled([
        fetchPositions(),
        fetchPositionCategories(),
        fetchCourses(),
        fetchCategories(),
        fetchEmployees(),
        fetchMatrix()
      ]);
      
      if (isMounted) {
        setIsInitialized(true);
      }
    };
    
    loadAllData();
    
    return () => {
      isMounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Определение доступных вкладок на основе прав
  const getAvailableTabs = () => {
    const tabs = [];

    // Сотрудники
    if (hasPermission(PERMISSIONS.SETTINGS_EMPLOYEES_TAB)) {
      tabs.push({ id: 'employees', label: 'Сотрудники', component: EmployeesTab });
    }

    // Должности
    if (hasPermission(PERMISSIONS.SETTINGS_POSITIONS_TAB)) {
      tabs.push({ id: 'positions', label: 'Должности', component: PositionsTab });
    }

    // Обучения
    if (hasPermission(PERMISSIONS.SETTINGS_TRAININGS_TAB)) {
      tabs.push({ id: 'trainings', label: 'Обучения', component: TrainingsTab });
    }

    // Матрица компетенций
    if (hasPermission(PERMISSIONS.SETTINGS_MATRIX_TAB)) {
      tabs.push({ id: 'matrix', label: 'Матрица компетенций', component: MatrixTab });
    }

    // Учетные записи - только для админа
    if (hasPermission(PERMISSIONS.SETTINGS_USERS_TAB)) {
      tabs.push({ id: 'users', label: 'Учетные записи', component: UsersTab });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  // Если текущая активная вкладка недоступна, переключаемся на первую доступную
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  if (showInitialLoading) {
    return (
      <div className="settings-page">
        <div className="loading-container">
          <div className="loading-card">
            <div className="loading-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v4" />
                <path d="M12 18v4" />
                <path d="M4.93 4.93l2.83 2.83" />
                <path d="M16.24 16.24l2.83 2.83" />
                <path d="M2 12h4" />
                <path d="M18 12h4" />
              </svg>
            </div>
            <h2>Загрузка справочников</h2>
            <p className="loading-message">
              Пожалуйста, подождите. Идет загрузка данных...
            </p>
            
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${loadingPercentage}%` }}
              >
                <span className="progress-percent">{loadingPercentage}%</span>
              </div>
            </div>
            
            <div className="progress-stats">
              <span>Загружено {loadedCount} из {totalCount}</span>
              <div className="spinner-small"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Функция рендера активной вкладки
  const renderActiveTab = () => {
    const activeTabConfig = availableTabs.find(tab => tab.id === activeTab);
    if (!activeTabConfig) return null;

    switch (activeTabConfig.id) {
      case 'employees':
        return (
          <EmployeesTab 
            positions={positionsData}
            departments={departments}
            employees={employeesData}
            isLoading={employeesStatus.loading && !isInitialized}
            onDataChange={() => handleDataChange(fetchEmployees, EVENTS.EMPLOYEE_UPDATED)}
          />
        );
      case 'positions':
        return (
          <PositionsTab 
            positions={positionsData}
            positionCategories={positionCategoriesData}
            departments={departments}
            isLoading={positionsStatus.loading && !isInitialized}
            onDataChange={() => handleDataChange(fetchPositions, EVENTS.POSITION_UPDATED)}
          />
        );
      case 'trainings':
        return (
          <TrainingsTab 
            courses={coursesData}
            categories={categoriesData}
            isLoading={coursesStatus.loading && !isInitialized}
            onDataChange={() => handleDataChange(fetchCourses, EVENTS.COURSE_UPDATED)}
          />
        );
      case 'matrix':
        return (
          <MatrixTab
            positions={positionsData}
            courses={coursesData}
            categories={categoriesData}
            matrix={matrixData}
            isLoading={matrixStatus.loading && !isInitialized}
            onDataChange={() => handleDataChange(fetchCourses)}
            onMatrixUpdate={updateMatrixAssignment}
          />
        );
      case 'users':
        return <UsersTab />;
      default:
        return null;
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-header-top">
          <div>
            <h1>Справочники и требования</h1>
            <p className="settings-subtitle">Управление справочниками и настройка матрицы компетенций</p>
          </div>
          <button 
            className="btn-refresh-all" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
              <path d="M21 3v6h-6" />
            </svg>
            {refreshing ? 'Обновление...' : 'Обновить все данные'}
          </button>
        </div>

        {/* Индикация состояния загрузки */}
        {!allDataLoaded && (
          <div className="data-status-bar">
            <div className="data-status-info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
              </svg>
              <span>Загрузка данных...</span>
            </div>
            
            <div className="data-status-text">
              <span className="status-badge inactive">
                Загружено {loadedCount} из {totalCount}
              </span>
            </div>
            
            <div className="data-status-progress">
              <div 
                className="data-status-fill" 
                style={{ width: `${loadingPercentage}%` }}
              />
            </div>
            
            <span className="data-status-info" style={{ fontSize: '12px' }}>
              {loadingPercentage}%
            </span>
          </div>
        )}
      </div>

      <div className="settings-tabs">
        {availableTabs.map((tab) => (
          <button 
            key={tab.id}
            className={activeTab === tab.id ? 'active' : ''} 
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.id === 'employees' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}
            {tab.id === 'positions' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            )}
            {tab.id === 'trainings' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            )}
            {tab.id === 'matrix' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            )}
            {tab.id === 'users' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                <path d="M20 21v-2a4 4 0 0 0-3-3.85" />
              </svg>
            )}
            {tab.label}
            {loadingStates[tab.id] && <span className="loading-dot"></span>}
          </button>
        ))}
      </div>

      {renderActiveTab()}
    </div>
  );
};

export default Settings;